import React, {
    forwardRef,
    useEffect,
    useRef,
    InputHTMLAttributes,
    useReducer,
    Dispatch,
    useImperativeHandle,
    useState,
} from 'react';
import './InputDate.css';
import inputDateReducer, {
    IDateData,
    TDateDataAction,
    defaultData,
} from './inputDateReducer';
import isElementIncluded from './util/isElementIncluded';
import InputBase, { IInputProps } from 'component-library/atoms/InputBase/InputBase';
import SuccessIcon from 'component-library/atoms/InputText/SuccessIcon/SuccessIcon';
import DynamicContainer from '../../../../../component-library/foundational/Layout/DynamicContainer';
import Label from '../../../../../component-library/atoms/Label';
import { addUnderscores } from 'component-library/utils/utils';
import { getSubtext } from 'component-library/utils/inputUtils';

export interface IDateInputProps extends IInputProps {
	requiredMessage?: string;
	restrictFutureDates?: boolean;
	labelClass?: string;
	/**
	 * The minimum allowable age. Defaults to 18 years.
	 */
	ageMin?: number;
	/**
	 * The maximum allowable age. Defaults to 100 years.
	 */
	ageMax?: number;
	isShowingHelperText?: boolean;
    isAuthorizedUser?: boolean;
    onKeyDown?: (e:React.KeyboardEvent<HTMLInputElement>) => void;
}

interface IInputDateFieldProps {
	kind: 'day' | 'month' | 'year';
	dateData: IDateData;
	inputProps: IDateInputProps;
	dispatch: Dispatch<TDateDataAction>;
	setHelperText: THandlerFunction;
    isAuthorizedUser?: boolean;
}

const InputDateField = forwardRef(
    ({ kind, dateData, inputProps, dispatch, setHelperText, isAuthorizedUser }: IInputDateFieldProps, ref) => {
        const inputIdentifier = inputProps.id || inputProps.label;
        const resolvedLabel =
			kind === 'day' ? 'dd' : kind === 'month' ? 'mm' : 'yyyy';
        const resolvedMaxLength = kind === 'year' ? 4 : 2;
        const tooltipProps =
			kind === 'year'
			    ? {
			        tooltip: inputProps.advanced ? inputProps.tooltip : '',
			        tooltipNotchPosition: inputProps.advanced
			            ? inputProps.tooltipNotchPosition
			            : undefined,

			        mobileTooltipNotchPosition: inputProps.advanced
			            ? inputProps.mobileTooltipNotchPosition
			            : null,
			    }
			    : {};
        const shouldSetValue = useRef<boolean>(false);
        const currentYear = useRef<number>(new Date().getFullYear());


        useEffect(() => {
            if (shouldSetValue && shouldSetValue.current) {
                shouldSetValue.current = false;
                inputProps.setValue(
                    `${dateData.month.value}/${dateData.day.value}/${dateData.year.value}`,
                );
            }
        }, [dateData[kind].value]);

        return (
            <InputBase
                ref={ref}
                {...inputProps}
                useSuccessIcon={false}
                showSuccessIcon={false}
                value={dateData[kind].value}
                className={'InputDate__month '}
                noErrorsOrHelper
                id={`${inputProps.id}__${ isAuthorizedUser ? 'au' : '' }${kind}`}
                hideAlertIcon
                label={inputProps.advanced ? resolvedLabel : ''}
                aria-describedby={`${inputIdentifier}__input_date${inputProps.advanced ? '__adv' : ''
                }__helper ${inputIdentifier}__input_date__desc`}
                aria-errormessage={`${inputIdentifier}__input_date__adv__helper`}
                aria-required={inputProps.required}
                aria-disabled={inputProps.disabled}
                error={
                    inputProps.error ||
					dateData.day.error ||
					dateData.month.error ||
					dateData.year.error
                }
                maxLength={resolvedMaxLength}
                aria-invalid={dateData[kind].valid}
                aria-label={`${kind.charAt(0).toUpperCase() + kind.slice(1)
                } Field`}
                {...tooltipProps}
                setValue={(newVal: string) => {
                    dispatch({
                        type: 'setValue',
                        target: kind,
                        payload: newVal.replace(/[^0-9]+/g, ''),
                        year: currentYear.current,
                        minAge: inputProps.ageMin ?? 18,
                        maxAge: inputProps.ageMax ?? 100,
                    });
                    shouldSetValue.current = true;
                }}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                    dispatch({ type: 'onFocus', target: kind });
                    if (
                        inputProps.valid &&
						dateData.day.valueAddAttempted &&
						dateData.month.valueAddAttempted &&
						dateData.year.valueAddAttempted
                    ) {
                        inputProps.valid[1](
                            dateData.day.valid &&
							dateData.month.valid &&
							dateData.year.valid,
                        );
                    }

                    setHelperText(prev => {
                        return {
                            ...prev,
                            [kind]: false,
                        };
                    });

                    if (inputProps.onFocus) {
                        inputProps.onFocus(e);
                    }
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    const relatedElement = e.relatedTarget as HTMLInputElement;
                    const isRelated = relatedElement && relatedElement instanceof HTMLInputElement && isElementIncluded(relatedElement.id);
                    dispatch({
                        type: 'onBlur',
                        target: kind,
                        options: {
                            isRelated,
                        },
                    });

                    if (dateData[kind].value) {
                        setHelperText(prev => {
                            return {
                                ...prev,
                                day: true,
                                month: true,
                                year: true,
                            };
                        });
                    }

                    if (
                        inputProps.valid &&
						((dateData.day.valueAddAttempted &&
							dateData.month.valueAddAttempted &&
							dateData.year.valueAddAttempted) ||
							(dateData.day.visited &&
								dateData.month.visited &&
								dateData.year.visited))
                    ) {
                        inputProps.valid[1](
                            dateData.day.valid &&
							dateData.month.valid &&
							dateData.year.valid,
                        );
                    }

                    if (inputProps.onBlur) {
                        inputProps.onBlur(e);
                    }
                }}
                onKeyDown={(e) => {
                    if (
                        !dateData[kind].valueAddAttempted &&
						[
						    '0',
						    '1',
						    '2',
						    '3',
						    '4',
						    '5',
						    '6',
						    '7',
						    '8',
						    '9',
						].includes(e.key)
                    ) {
                        dispatch({ type: 'valueAddAttempted', target: kind });
                    }
                    if (inputProps.onKeyDown) {
                        inputProps.onKeyDown(e);
                    }
                }}
                required={false}
                valid={undefined}
                inputMode='numeric'
            />
        );
    },
);

const InputDate = forwardRef(
    (
        props: IDateInputProps & InputHTMLAttributes<any>,
        ref: any,
    ) => {
        const [dateData, dispatch] = useReducer(inputDateReducer, defaultData());
        const { day, month, year, invalidErrorMessage } =
			dateData ?? defaultData();
        const aggregateError = day.error || month.error || year.error;

        const dayRef = useRef<HTMLInputElement>(null);
        const monthRef = ref;
        const yearRef = useRef<HTMLInputElement>(null);        

        const [helperText, setHelperText] = useState<Record<string, boolean>>({
            month: false,
            day: false,
            year: false,
        });

        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

        const getSuccessIcon = () => {
            const { month, day, year } = dateData;
            const visited = day.visited && month.visited && year.visited;
            const focused = day.focused || month.focused || year.focused;

            return <SuccessIcon showIcon={props.showSuccessIcon && visited && !focused} />;
        };

        useImperativeHandle(
            ref,
            () => ({
                clearFields: (shouldError: boolean = false) => {
                    dispatch({
                        type: 'clear',
                        payload: { error: shouldError },
                    });
                    props.setValue('');
                    if (props.valid) {
                        props.valid[1](!shouldError);
                    }
                },
            }),
            [],
        );

        // uncertain as to why this is in a useEffect and not part of each InputBase instance
        useEffect(() => {
            if (props.onChange) {
                props.onChange(`${month.value}/${day.value}/${year.value}`);
            }
        }, [dateData]);

        useEffect(() => {
            if (dateData.position === 'day') {
				dayRef.current?.focus();
            }
            if (dateData.position === 'year') {
				yearRef.current?.focus();
            }
        }, [dateData.position]);

        useEffect(() => {
            if (props.value) {
                dispatch({ type: 'initialize', payload: props.value });
            }
        }, []);

        return (
            <DynamicContainer displayTag={props.useSuccessIcon} className='InputDate__wrapper' tag='div'>
                <div
                    className={`InputDate ${props.className || ''} ${props.disabled ? 'InputDate__disabled' : ''
                    }`}
                    data-cl='13'
                    id={props.isAuthorizedUser ? 'auDob' : 'dob'}
                >
                    {!props.advanced && (
                        <Label
                            helperText={props.helperText}
                            className={props.labelClass}
                            tooltip={props.tooltip}
                            tooltipAlign={props.tooltipNotchPosition}
                            mobileTooltipAlign={props.mobileTooltipNotchPosition}
                            required={props.removeAsterisk ? false : props.required}
                            alternate
                            error={aggregateError || props.error}
                        >
                            {props.label}
                        </Label>
                    )}
                    <div className='InputDate__group'>
                        <div className={'InputDate__container'}>
                            <InputDateField
                                kind='month'
                                dateData={dateData || defaultData()}
                                dispatch={dispatch}
                                inputProps={props}
                                ref={monthRef}
                                setHelperText={setHelperText}
                                isAuthorizedUser={props.isAuthorizedUser}
                            />
                            {props.isShowingHelperText && helperText.month &&
								dateData.month.value.length === 2 &&
								<span className={'InputDate__helperText'}>
								    {monthNames[Number(dateData.month.value) - 1]}
								</span>}
                        </div>
                        <p className='InputDate__separator'>-</p>
                        <div className={'InputDate__container'}>
                            <InputDateField
                                kind='day'
                                dateData={dateData || defaultData()}
                                dispatch={dispatch}
                                inputProps={props}
                                ref={dayRef}
                                setHelperText={setHelperText}
                                isAuthorizedUser={props.isAuthorizedUser}
                            />
                            {props.isShowingHelperText && helperText.day &&
								dateData.day.value.length === 2 &&
								<span className={'InputDate__helperText'}>
								    {dateData.day.value}
								</span>}
                        </div>
                        <p className='InputDate__separator'>-</p>
                        <div className={'InputDate__container'}>
                            <InputDateField
                                kind='year'
                                dateData={dateData || defaultData()}
                                dispatch={dispatch}
                                inputProps={props}
                                ref={yearRef}
                                setHelperText={setHelperText}
                                isAuthorizedUser={props.isAuthorizedUser}
                            />
                            {props.isShowingHelperText && helperText.year &&
								dateData.year.value.length === 4 &&
								<span className={'InputDate__helperText'}>{dateData.year.value}</span>}
                        </div>
                    </div>
                    <p
                        className={`InputDate__error_text ${aggregateError || props.error ? 'error' : ''
                        }${props.showExclamationError ? 'WithExclamation' : ''}`}
                        id={`${addUnderscores(props.id, props.label?.toString())}__input_date__adv__helper`}
                        aria-live='assertive'
                    >
                        {getSubtext({
                            validationError: aggregateError,
                            errorMessage:
								props.requiredMessage ??
								props.errorText ??
								invalidErrorMessage,
                            error: props.error,
                            errorText:
								props.errorText ??
								'Please provide a valid Date of Birth',
                            advanced: props.advanced,
                            helperText: props.helperText,
                            description: props.description,
                        })}
                    </p>
                </div>
                {props.useSuccessIcon && getSuccessIcon()}
            </DynamicContainer>
        );
    },
);

InputDate.displayName = 'InputDate';

export default InputDate;
